import { Box } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useScoutConversation } from "../hooks/useScoutConversation";
import { ChatChrome } from "./ChatChrome";
import { NewChat } from "./NewChat";

export const ScoutChat = ({
  onHasMessagesChange,
  onConversationTitleChange,
}: {
  onHasMessagesChange?: (hasMessages: boolean) => void;
  onConversationTitleChange?: (input: {
    conversationId: string | undefined;
    title: string | undefined;
  }) => void;
}) => {
  const navigate = useNavigate();
  const { tenantId, conversationId } = useParams();
  const handleConversationIdChange = useCallback(
    (nextConversationId: string) => {
      if (!tenantId) {
        return;
      }

      navigate(`/o/${tenantId}/scout/${nextConversationId}`, { replace: true });
    },
    [navigate, tenantId]
  );
  const {
    conversation,
    messages,
    activeTurn,
    streamStatus,
    loading,
    isSending,
    isAborting,
    isLoadingOlderMessages,
    hasOlderMessages,
    sendMessage,
    abortTurn,
    loadOlderMessages,
  } = useScoutConversation({
    tenantId,
    conversationId,
    onConversationIdChange: handleConversationIdChange,
  });
  const hasMessages = messages.length > 0;
  const isConversationRoute = Boolean(conversationId);
  const isLoadingConversation = isConversationRoute && loading && !hasMessages;
  useEffect(() => {
    onHasMessagesChange?.(hasMessages);
  }, [hasMessages, onHasMessagesChange]);
  useEffect(() => {
    onConversationTitleChange?.({
      conversationId: conversation?.id,
      title: conversation?.title,
    });
  }, [conversation?.id, conversation?.title, onConversationTitleChange]);
  const isTurnRunning =
    isSending ||
    isAborting ||
    activeTurn?.status === "starting" ||
    activeTurn?.status === "streaming";

  const [allowMutations, setAllowMutations] = useState<boolean>(false);
  const userTouchedRef = useRef(false);
  // Track the previous URL conversation id to distinguish true navigation
  // (id-A → id-B) from the post-NewChat URL update (undefined → id).
  const lastUrlConvIdRef = useRef<string | undefined>(conversationId);

  // Handle conversation navigation and the undefined→id post-NewChat transition.
  // Only resets local state on a true conversation switch (both ids defined and
  // different). Skips the undefined→id case so the user's pre-submit toggle
  // choice is preserved across the NewChat→ChatChrome transition.
  useEffect(() => {
    const prev = lastUrlConvIdRef.current;
    lastUrlConvIdRef.current = conversationId;

    // undefined → some-id: user just submitted their first message. The
    // optimistic conversation has allowMutations: false by default, but the
    // user's local toggle state is what they intended. Preserve it.
    if (!prev) {
      return;
    }
    // URL didn't change — no-op.
    if (prev === conversationId) {
      return;
    }

    // True navigation to a different conversation: reset and sync from server.
    userTouchedRef.current = false;
    setAllowMutations(conversation?.allowMutations ?? false);
  }, [conversationId, conversation?.allowMutations]);

  // Server-driven updates (e.g., worker mirrors toggle state to the durable
  // row, history refetches). Only applied when the user hasn't touched the
  // toggle in this session — local state takes precedence after a touch.
  useEffect(() => {
    if (!userTouchedRef.current) {
      setAllowMutations(conversation?.allowMutations ?? false);
    }
  }, [conversation?.allowMutations]);

  const handleAllowMutationsChange = useCallback((next: boolean) => {
    userTouchedRef.current = true;
    setAllowMutations(next);
  }, []);

  const handleSubmit = useCallback(
    async (
      message: string,
      attachments?: Array<{
        privateDocumentId: string;
        filename: string;
        mediaType: string;
        sizeBytes: number;
        signedDownloadUrl?: string;
      }>
    ) => {
      await sendMessage(message, {
        allowMutations,
        attachmentPreviews: attachments?.map((attachment) => ({
          privateDocumentId: attachment.privateDocumentId,
          filename: attachment.filename,
          mediaType: attachment.mediaType,
          sizeBytes: attachment.sizeBytes,
          signedDownloadUrl: attachment.signedDownloadUrl,
        })),
      });
    },
    [allowMutations, sendMessage]
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        alignItems: "center",
      }}
    >
      {!hasMessages && !isConversationRoute ? (
        <NewChat
          tenantId={tenantId}
          onSubmit={handleSubmit}
          onAbort={abortTurn}
          isSending={isTurnRunning}
          isAborting={isAborting}
          allowMutations={allowMutations}
          onAllowMutationsChange={handleAllowMutationsChange}
        />
      ) : (
        <ChatChrome
          tenantId={tenantId}
          conversationId={conversationId}
          messages={messages}
          activeTurn={activeTurn}
          streamStatus={streamStatus}
          loading={isLoadingConversation}
          initialPinned={isConversationRoute}
          onSubmit={handleSubmit}
          onAbort={abortTurn}
          isSending={isTurnRunning}
          isAborting={isAborting}
          onLoadOlderMessages={loadOlderMessages}
          isLoadingOlderMessages={isLoadingOlderMessages}
          hasOlderMessages={hasOlderMessages}
          allowMutations={allowMutations}
          onAllowMutationsChange={handleAllowMutationsChange}
        />
      )}
    </Box>
  );
};
